/**
 * Prop-firm rule presets.
 *
 * ## These are pre-fills, never figures
 *
 * The questionnaire shows every number from here on a screen that requires the
 * user to confirm it, and the copy says plainly that AEGIS does not track firm
 * terms. That is not a disclaimer bolted on — it is the whole reason a preset
 * is safe to ship. Firms change terms without notice, and a stale number that
 * reached the rule engine unread would be a wrong risk figure on the screen a
 * trader checks before sizing up.
 *
 * ## Verified or label-only, never guessed
 *
 * A firm either has a preset researched against its published terms, with the
 * month recorded — the same discipline `docs/` applies to vendor pricing — or
 * it appears with `preset: null` and pre-fills nothing, opening the numbers
 * screen empty.
 *
 * Shipping eight roughly-remembered presets would have made the grid look more
 * complete and put five plausible wrong numbers into circulation. Four
 * researched entries and a longer label-only list is the honest version of the
 * same screen.
 *
 * ## This is data
 *
 * In production these seed a `prop_rule_sets` table, editable without a
 * deploy, exactly as `presets.ts` describes. Adding a firm here is a row, not
 * a release.
 */
import { dec, type Decimal } from '../money.js';
import type { DailyLossBasis, DrawdownType } from '../rules/types.js';

/**
 * One firm's terms at one account size, as ratios of the account.
 *
 * Ratios rather than absolute dollars because firms scale their tiers almost
 * linearly, and storing nine sizes per firm would be nine chances for one to
 * drift. The exceptions to linearity are exactly the sort of thing the
 * confirmation screen exists to catch.
 */
export interface FirmPreset {
  /** Profit target as a share of the account. */
  readonly targetRatio: number;
  /**
   * Daily loss limit as a share of the account, or null for none.
   *
   * Null is a real answer, not a missing one: several firms genuinely have no
   * daily loss limit, and `0` would read as "you may not lose anything".
   */
  readonly dailyLossRatio: number | null;
  readonly drawdownRatio: number;
  readonly drawdownType: DrawdownType;
  /**
   * Where the trailing floor stops, as a multiple of the account.
   *
   * Every firm researched here freezes at starting balance plus one full
   * drawdown — the floor then sits exactly on the starting balance. Stored per
   * firm anyway, because the next one to be added may not.
   */
  readonly freezeAtRatio: number | null;
  readonly dailyLossBasis: DailyLossBasis;
  readonly minTradingDays: number;
  /** Largest share of total profit one day may carry, as 0..1. Zero for none. */
  readonly consistency: number;
}

export interface Firm {
  readonly id: string;
  readonly name: string;
  /** One line describing the drawdown, for the picker. */
  readonly summary: string;
  /** Null means label only — the numbers screen opens empty. */
  readonly preset: FirmPreset | null;
  /** The month the terms were read, and where. Absent for label-only firms. */
  readonly researched?: string;
}

/**
 * The catalogue.
 *
 * Verified entries first, because a picker whose first rows pre-fill teaches
 * the user what the screen does before they reach a row that does not.
 */
export const FIRMS: readonly Firm[] = [
  {
    id: 'topstep',
    name: 'Topstep',
    summary: 'Trailing, end of day · freezes at start + drawdown',
    researched: 'August 2026',
    preset: {
      targetRatio: 0.06, dailyLossRatio: 0.02, drawdownRatio: 0.04,
      drawdownType: 'trailing_eod', freezeAtRatio: 1.04,
      dailyLossBasis: 'realized_plus_unrealized', minTradingDays: 2, consistency: 0.5,
    },
  },
  {
    id: 'apex',
    name: 'Apex Trader Funding',
    summary: 'Trailing intraday · no daily loss limit on intraday plans',
    researched: 'August 2026',
    preset: {
      targetRatio: 0.06, dailyLossRatio: null, drawdownRatio: 0.05,
      drawdownType: 'trailing_intraday', freezeAtRatio: 1.05,
      dailyLossBasis: 'realized_plus_unrealized', minTradingDays: 1, consistency: 0.3,
    },
  },
  {
    id: 'tpt',
    name: 'Take Profit Trader',
    summary: 'No daily loss limit · trailing turns intraday once funded',
    researched: 'August 2026',
    preset: {
      targetRatio: 0.06, dailyLossRatio: null, drawdownRatio: 0.04,
      drawdownType: 'trailing_eod', freezeAtRatio: 1.04,
      dailyLossBasis: 'realized_plus_unrealized', minTradingDays: 5, consistency: 0,
    },
  },
  // Label-only. Common enough that their absence would read as a gap, not
  // researched closely enough to pre-fill a number somebody trades against.
  { id: 'mffu',     name: 'My Funded Futures',    summary: 'Enter your plan’s numbers', preset: null },
  { id: 'tradeify', name: 'Tradeify',             summary: 'Enter your plan’s numbers', preset: null },
  { id: 'bulenox',  name: 'Bulenox',              summary: 'Enter your plan’s numbers', preset: null },
  { id: 'etf',      name: 'Elite Trader Funding', summary: 'Enter your plan’s numbers', preset: null },
  { id: 'tradeday', name: 'TradeDay',             summary: 'Enter your plan’s numbers', preset: null },
  { id: 'other',    name: 'Other / not listed',   summary: 'Enter your firm’s numbers', preset: null },
];

export function findFirm(id: string | null | undefined): Firm | undefined {
  return id ? FIRMS.find((f) => f.id === id) : undefined;
}

/** The account sizes the picker offers. Any other value is typed in. */
export const ACCOUNT_SIZES: readonly number[] = [25_000, 50_000, 100_000, 150_000, 250_000];

/** What a firm's preset works out to at one account size. */
export interface FirmNumbers {
  readonly profitTarget: Decimal | null;
  readonly maxDailyLoss: Decimal | null;
  readonly maxDrawdown: Decimal | null;
  readonly drawdownType: DrawdownType;
  readonly drawdownFreezeAt: Decimal | null;
  readonly dailyLossBasis: DailyLossBasis;
  readonly minTradingDays: number;
  readonly consistency: number;
}

/**
 * A firm's terms at one account size.
 *
 * Neutral rather than empty for a label-only firm: the drawdown type still has
 * to be *something* for the form to render, and `static` is the one that
 * claims least — a floor that does not move cannot be wrong about how it
 * moves.
 */
export function firmNumbers(firm: Firm | undefined, size: number): FirmNumbers {
  const p = firm?.preset;
  if (!p || !Number.isFinite(size) || size <= 0) {
    return {
      profitTarget: null, maxDailyLoss: null, maxDrawdown: null,
      drawdownType: 'static', drawdownFreezeAt: null,
      dailyLossBasis: 'realized', minTradingDays: 0, consistency: 0,
    };
  }
  const at = (r: number): Decimal => dec(size).times(r);
  return {
    profitTarget: at(p.targetRatio),
    maxDailyLoss: p.dailyLossRatio === null ? null : at(p.dailyLossRatio),
    maxDrawdown: at(p.drawdownRatio),
    drawdownType: p.drawdownType,
    drawdownFreezeAt: p.freezeAtRatio === null ? null : at(p.freezeAtRatio),
    dailyLossBasis: p.dailyLossBasis,
    minTradingDays: p.minTradingDays,
    consistency: p.consistency,
  };
}
